/**
 * Web Push, VAPID, children only (SPEC §10, CLAUDE.md rule 5).
 *
 * Telegram is a parent channel and stops at the parents. Baya and Mehdi have no
 * account and no application there; their reminders arrive here, in the PWA,
 * and nowhere else.
 *
 * The payload constraint is enforced on the receiving side, in `sw.js`: a
 * notification is legible on a locked screen, so it may say that there is work
 * to do — never what it is about, never a mark, never a name.
 *
 * `notifier` owns the `push_subscription` table (§11); `core-api` does not
 * expose a route for it yet. Rather than invent one, this module reports
 * `server-unavailable` and the settings screen says so.
 */

const SUBSCRIPTION_ENDPOINT = "/api/push/subscriptions";

export type PushStatus =
  | "unknown"
  | "unsupported"
  | "denied"
  | "unsubscribed"
  | "subscribed"
  | "server-unavailable";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

function supported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function pushStatus(): Promise<PushStatus> {
  if (!supported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  return existing ? "subscribed" : "unsubscribed";
}

export async function subscribeToPush(): Promise<PushStatus> {
  if (!supported()) return "unsupported";
  if (!VAPID_PUBLIC_KEY) return "server-unavailable";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    }));

  try {
    const response = await fetch(SUBSCRIPTION_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(subscription.toJSON()),
    });
    if (response.status === 404 || response.status === 405) return "server-unavailable";
    if (!response.ok) return "server-unavailable";
  } catch {
    return "server-unavailable";
  }
  return "subscribed";
}
